<script lang="ts">
export default {
  name: 'Tabs',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { TabsRoot, TabsList, TabsTrigger, TabsContent } from 'reka-ui';

export interface TabItem {
  value: string;
  label: string;
  icon?: any;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    items: TabItem[];
    variant?: 'bordered' | 'lifted' | 'boxed';
    class?: string;
  }>(),
  {
    variant: 'bordered',
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const currentTab = computed({
  get: () => props.modelValue || props.items[0]?.value || '',
  set: (value) => emit('update:modelValue', value),
});

const tabsRootClasses = computed(() => {
  const classes = ['w-full'];
  
  if (props.class) {
    classes.push(props.class);
  }
  
  return classes.join(' ');
});

const tabsListClasses = computed(() => {
  const classes = ['tabs', 'flex', 'w-full'];
  
  switch (props.variant) {
    case 'lifted':
      classes.push('tabs-lift');
      break;
    case 'boxed':
      classes.push('tabs-box');
      break;
    case 'bordered':
    default:
      classes.push('tabs-border');
      break;
  }
  
  return classes.join(' ');
});
</script>

<template>
  <TabsRoot v-model="currentTab" :class="tabsRootClasses">
    <TabsList :class="tabsListClasses">
      <TabsTrigger
        v-for="item in items"
        :key="item.value"
        :value="item.value"
        :disabled="item.disabled"
        class="tab [--tab-border-color:#d3d3d3] font-medium"
        :class="{ 'tab-active': currentTab === item.value }"
      >
        <component v-if="item.icon" :is="item.icon" class="w-4 h-4 mr-1" />
        {{ item.label }}
      </TabsTrigger>
      <span class="grow border-b border-[#d3d3d3]"></span>
    </TabsList>

    <!-- Single content slot: when provided, render once (e.g. for router-view). -->
    <div v-if="$slots.content" class="mt-0">
      <slot name="content" />
    </div>
    <!-- Per-tab content: named slots or default slot with :item -->
    <TabsContent
      v-else
      v-for="item in items"
      :key="`content-${item.value}`"
      :value="item.value"
      class=""
    >
      <slot :name="item.value">
        <slot name="default" :item="item" />
      </slot>
    </TabsContent>
  </TabsRoot>
</template>
